import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, Button, Alert,
  Skeleton, MenuItem,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { authApi } from '@/api/endpoints/auth';
import { portalApi, employeeAllowancesApi } from '@/api/endpoints/hr';
import { formatCurrency, extractErrorMessage } from '@/utils/format';
import { useJurisdiction } from '@/hooks/useJurisdiction';
import AddressForm from '@/components/common/AddressForm';
import PhoneInput from '@/components/common/PhoneInput';
import { InfoRow, SectionLabel } from './shared';

/** Compensation card with fixed + dynamic allowances (read-only for staff). */
function CompensationCard({ emp, currencyCode }: { emp: any; currencyCode: string }) {
  const { data: dynamicAllowances } = useQuery({
    queryKey: ['employee-allowances', emp.id],
    queryFn: () => employeeAllowancesApi.list(emp.id).then(r => r.data),
    enabled: !!emp.id,
  });

  const activeAllowances = (dynamicAllowances || []).filter((a: any) => a.is_active);
  const dynamicTotal = activeAllowances.reduce((s: number, a: any) => s + Number(a.amount), 0);
  const gross = Number(emp.basic_salary) + Number(emp.housing_allowance) + Number(emp.transport_allowance) + Number(emp.meal_allowance) + dynamicTotal;

  return (
    <Card>
      <CardContent>
        <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 1 }}>Compensation</Typography>
        <InfoRow label="Basic Salary" value={formatCurrency(emp.basic_salary, currencyCode)} />
        <InfoRow label="Housing Allowance" value={formatCurrency(emp.housing_allowance, currencyCode)} />
        <InfoRow label="Transport Allowance" value={formatCurrency(emp.transport_allowance, currencyCode)} />
        <InfoRow label="Meal Allowance" value={formatCurrency(emp.meal_allowance, currencyCode)} />
        {activeAllowances.map((a: any) => (
          <InfoRow key={a.id} label={a.allowance_type_name || 'Allowance'} value={formatCurrency(a.amount, currencyCode)} />
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, mt: 0.5, borderTop: '2px solid', borderColor: 'primary.main' }}>
          <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>Gross Pay</Typography>
          <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold, color: 'primary.main' }}>{formatCurrency(gross, currencyCode)}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

interface PersonalTabProps {
  user: any;
  emp: any;
  activeCompany: any;
  meLoading: boolean;
  empLoading: boolean;
}

export default function PersonalTab({ user, emp, activeCompany, meLoading, empLoading }: PersonalTabProps) {
  const { currencyCode } = useJurisdiction();
  const qc = useQueryClient();

  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '',
    personal_email: '',
    address_line_1: '', address_line_2: '', city: '', lga: '', state: '', postal_code: '', country: 'NG',
    marital_status: '', blood_group: '',
    emergency_contact_name: '', emergency_contact_phone: '',
    emergency_contact_relationship: '',
    emergency_contact_address_line_1: '', emergency_contact_address_line_2: '',
    emergency_contact_city: '', emergency_contact_lga: '', emergency_contact_state: '',
    emergency_contact_postal_code: '', emergency_contact_country: 'NG',
  });
  const [loaded, setLoaded] = useState(false);
  const [empLoaded, setEmpLoaded] = useState(false);
  const [msg, setMsg] = useState({ type: '' as 'success' | 'error' | '', text: '' });

  useEffect(() => {
    if (user && !loaded) {
      setForm(p => ({ ...p, first_name: user.first_name || '', last_name: user.last_name || '', phone: (user as any).phone || '' }));
      setLoaded(true);
    }
  }, [user, loaded]);

  useEffect(() => {
    if (emp && !empLoaded) {
      const e = emp as any;
      setForm(p => ({
        ...p,
        personal_email: e.personal_email || '',
        address_line_1: e.address_line_1 || '', address_line_2: e.address_line_2 || '',
        city: e.city || '', lga: e.lga || '', state: e.state || '',
        postal_code: e.postal_code || '', country: e.country || 'NG',
        marital_status: e.marital_status || '', blood_group: e.blood_group || '',
        emergency_contact_name: e.emergency_contact_name || '',
        emergency_contact_phone: e.emergency_contact_phone || '',
        emergency_contact_relationship: e.emergency_contact_relationship || '',
        emergency_contact_address_line_1: e.emergency_contact_address_line_1 || '',
        emergency_contact_address_line_2: e.emergency_contact_address_line_2 || '',
        emergency_contact_city: e.emergency_contact_city || '',
        emergency_contact_lga: e.emergency_contact_lga || '',
        emergency_contact_state: e.emergency_contact_state || '',
        emergency_contact_postal_code: e.emergency_contact_postal_code || '',
        emergency_contact_country: e.emergency_contact_country || 'NG',
      }));
      setEmpLoaded(true);
    }
  }, [emp, empLoaded]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await authApi.updateMe({ first_name: form.first_name, last_name: form.last_name, phone: form.phone });
      if (emp?.id) {
        await portalApi.updateMyProfile({
          personal_email: form.personal_email,
          address_line_1: form.address_line_1, address_line_2: form.address_line_2,
          city: form.city, lga: form.lga, state: form.state,
          postal_code: form.postal_code, country: form.country,
          marital_status: form.marital_status, blood_group: form.blood_group,
          emergency_contact_name: form.emergency_contact_name,
          emergency_contact_phone: form.emergency_contact_phone,
          emergency_contact_relationship: form.emergency_contact_relationship,
          emergency_contact_address_line_1: form.emergency_contact_address_line_1,
          emergency_contact_address_line_2: form.emergency_contact_address_line_2,
          emergency_contact_city: form.emergency_contact_city,
          emergency_contact_lga: form.emergency_contact_lga,
          emergency_contact_state: form.emergency_contact_state,
          emergency_contact_postal_code: form.emergency_contact_postal_code,
          emergency_contact_country: form.emergency_contact_country,
        });
      }
    },
    onSuccess: () => { setMsg({ type: 'success', text: 'Profile saved.' }); qc.invalidateQueries({ queryKey: ['auth/me'] }); },
    onError: (e: any) => setMsg({ type: 'error', text: extractErrorMessage(e, 'Failed.') }),
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (profileLocked) { setMsg({ type: 'error', text: 'Your profile information is locked after employment. Contact HR to request an unlock.' }); return; }
    if (!form.first_name.trim() || !form.last_name.trim()) { setMsg({ type: 'error', text: 'Name required.' }); return; }
    saveMutation.mutate();
  };

  const f = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  // Self-service profile lock — set at employment; only HR can unlock.
  // Synthetic profiles (no employee record) are never locked.
  const profileLocked = !!(emp as any)?.profile_locked;

  return (
    <Grid container spacing={2.5}>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 2 }}>Edit Profile</Typography>
            {profileLocked && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your profile information is locked after employment. Contact HR to request an unlock.
              </Alert>
            )}
            {msg.text && <Alert severity={msg.type || 'info'} sx={{ mb: 2 }}>{msg.text}</Alert>}
            {meLoading ? <Skeleton variant="rectangular" height={200} /> : (
              <Box component="form" onSubmit={handleSave}>
                {/* fieldset propagates disabled to every native input inside */}
                <Box component="fieldset" disabled={profileLocked} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField label="First Name" fullWidth value={form.first_name} onChange={e => f('first_name', e.target.value)} required /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField label="Last Name" fullWidth value={form.last_name} onChange={e => f('last_name', e.target.value)} required /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField label="Work Email" fullWidth value={user?.email || ''} disabled helperText="Contact admin to change" /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField label="Personal Email" fullWidth value={form.personal_email} onChange={e => f('personal_email', e.target.value)} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><PhoneInput label="Phone" value={form.phone} onChange={v => f('phone', v)} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField label="Marital Status" select fullWidth value={form.marital_status} onChange={e => f('marital_status', e.target.value)}>
                      {['', 'Single', 'Married', 'Divorced', 'Widowed'].map(s => <MenuItem key={s} value={s}>{s || '—'}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField label="Blood Group" select fullWidth value={form.blood_group} onChange={e => f('blood_group', e.target.value)}>
                      {['', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(s => <MenuItem key={s} value={s}>{s || '—'}</MenuItem>)}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12 }}><SectionLabel>Address</SectionLabel></Grid>
                  <Grid size={{ xs: 12 }}>
                    <AddressForm
                      values={{ address_line_1: form.address_line_1, address_line_2: form.address_line_2, city: form.city, lga: form.lga, state: form.state, postal_code: form.postal_code, country: form.country }}
                      onChange={(field, value) => f(field, value)}
                      defaultCountryCode={form.country || 'NG'}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}><SectionLabel>Emergency Contact / Next of Kin</SectionLabel></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField label="Name" fullWidth value={form.emergency_contact_name} onChange={e => f('emergency_contact_name', e.target.value)} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Relationship" select fullWidth value={form.emergency_contact_relationship} onChange={e => f('emergency_contact_relationship', e.target.value)}>
                      {['', 'Spouse', 'Parent', 'Child', 'Sibling', 'Guardian', 'Friend', 'Cousin', 'Uncle', 'Aunt', 'Other'].map(r => <MenuItem key={r} value={r}>{r || '—'}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><PhoneInput label="Phone" value={form.emergency_contact_phone} onChange={v => f('emergency_contact_phone', v)} /></Grid>
                  <Grid size={{ xs: 12 }}>
                    <AddressForm
                      values={{ address_line_1: form.emergency_contact_address_line_1, address_line_2: form.emergency_contact_address_line_2, city: form.emergency_contact_city, lga: form.emergency_contact_lga, state: form.emergency_contact_state, postal_code: form.emergency_contact_postal_code, country: form.emergency_contact_country }}
                      onChange={(field, value) => f(`emergency_contact_${field}`, value)}
                      defaultCountryCode={form.emergency_contact_country || 'NG'}
                      compact
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saveMutation.isPending || profileLocked} sx={{ mt: 1 }}>
                      {saveMutation.isPending ? 'Saving...' : profileLocked ? 'Locked by HR' : 'Save Changes'}
                    </Button>
                  </Grid>
                </Grid>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Sidebar — read-only work info */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, mb: 1 }}>Work Information</Typography>
            {empLoading ? <Skeleton height={140} /> : emp ? (
              <>
                <InfoRow label="Ref" value={emp.employee_ref} />
                <InfoRow label="Designation" value={emp.designation} />
                <InfoRow label="Status" value={emp.employment_status} />
                <InfoRow label="Hired" value={emp.date_of_hire} />
                <InfoRow label="Company" value={activeCompany?.company_name} />
              </>
            ) : (
              <>
                <InfoRow label="Role" value={activeCompany?.role_name} />
                <InfoRow label="Company" value={activeCompany?.company_name} />
                <InfoRow label="Email" value={user?.email} />
              </>
            )}
          </CardContent>
        </Card>
        {emp && (
          <CompensationCard emp={emp} currencyCode={currencyCode} />
        )}
      </Grid>
    </Grid>
  );
}
